import { useState } from 'react';
import { PageHeader } from '@solid/ui';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ClipboardList, Loader2, Plus, Settings2 } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/utils';
import { formsApi, type FormListItem } from '@/lib/api';
import { FormListRow, useOpenForm } from '@/components/forms/FormListRow';
import { FORM_STATUS_CLASSES, FORM_STATUS_LABELS } from '@/components/forms/form-utils';
import { NewFormModal } from '@/components/forms/NewFormModal';

interface ProjectLayoutContext {
  isAgencyMember?: boolean;
  userId?: string;
  project?: { id: string; name: string; archivedAt?: string | null };
}

/**
 * Project → Forms. Members see what they can fill (assigned to them, or open);
 * agency members see every form in the project and manage from here.
 */
export function ProjectForms() {
  const { projectId } = useParams<{ projectId: string }>();
  const { isAgencyMember, project } = useOutletContext<ProjectLayoutContext>() || {};
  const [showNew, setShowNew] = useState(false);
  const isArchived = !!project?.archivedAt;

  const { data: forms, isLoading, isError, refetch } = useQuery({
    queryKey: ['projectForms', projectId],
    queryFn: () => formsApi.listForProject(projectId!),
    enabled: !!projectId,
  });

  return (
    <div className="w-full">
      <PageHeader
        title="Forms"
        subtitle={
          isAgencyMember
            ? 'Questionnaires and intake forms for this project.'
            : 'Forms the Solid Digital team has asked you to complete.'
        }
        actions={
          isAgencyMember && !isArchived && (
            <Button onClick={() => setShowNew(true)}>
              <Plus className="w-4 h-4 mr-2" />
              New form
            </Button>
          )
        }
      />

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 text-grey-400 animate-spin" />
        </div>
      ) : isError ? (
        <Card>
          <p className="text-sm text-secondary">Couldn't load forms.</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
            Retry
          </Button>
        </Card>
      ) : !forms || forms.length === 0 ? (
        <Card padding="none">
          <EmptyState
            icon={ClipboardList}
            title={isAgencyMember ? 'No forms yet' : 'Nothing to fill out'}
            description={
              isAgencyMember
                ? 'Build a questionnaire, publish it, and assign it to the people whose answers you need.'
                : "When the team needs information from you, the form will show up here."
            }
            action={
              isAgencyMember && !isArchived ? (
                <Button onClick={() => setShowNew(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  New form
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : isAgencyMember ? (
        <StaffList forms={forms} isArchived={isArchived} />
      ) : (
        <Card padding="none">
          <div className="divide-y divide-grey-100">
            {forms.map((f) => (
              <FormListRow
                key={f.id}
                formId={f.id}
                title={f.title}
                description={f.description}
                myResponse={f.myDraft ?? f.myLastSubmitted}
                dueDate={f.myDraft?.dueDate}
                repeatable={f.openSubmissions}
                mySubmittedCount={f.mySubmittedCount}
              />
            ))}
          </div>
        </Card>
      )}

      {showNew && projectId && <NewFormModal projectId={projectId} projectName={project?.name} onClose={() => setShowNew(false)} />}
    </div>
  );
}

/**
 * Agency view: every form, managed from here. Staff are respondents too
 * (Jesse, 2026-11-04) — a published form that's open, or assigned to them,
 * gets a fill button beside Manage, so the row is no longer one big link.
 */
function StaffList({ forms, isArchived }: { forms: FormListItem[]; isArchived: boolean }) {
  const { open, startingId } = useOpenForm();
  return (
    <Card padding="none">
      <div className="divide-y divide-grey-100">
        {forms.map((f) => {
          const total = f.counts.pending + f.counts.draft + f.counts.submitted;
          return (
            <FormListRow
              key={f.id}
              formId={f.id}
              title={f.title}
              description={f.description}
              meta={
                <>
                  <span className={cn('px-2 py-0.5 rounded-full font-medium', FORM_STATUS_CLASSES[f.status])}>{FORM_STATUS_LABELS[f.status]}</span>
                  {f.openSubmissions && <span>Open to all members</span>}
                  <span className="tabular-nums">
                    {f.counts.submitted} submitted{total ? ` · ${f.counts.pending + f.counts.draft} outstanding` : ''}
                  </span>
                  {f.myDraft && <span className="text-accent">{f.myDraft.status === 'pending' ? 'Assigned to you' : 'You have a draft'}</span>}
                </>
              }
              right={
                <div className="flex items-center gap-2">
                  {f.status === 'published' && !isArchived && (f.openSubmissions || f.myDraft) && (
                    <button
                      type="button"
                      onClick={() => open(f.id, f.myDraft)}
                      disabled={startingId === f.id}
                      className="btn-outline btn-sm shrink-0 inline-flex items-center gap-1.5"
                    >
                      {startingId === f.id && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                      {f.myDraft && f.myDraft.status !== 'pending' ? 'Continue' : f.mySubmittedCount > 0 ? 'Submit another' : 'Fill out'}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <Link to={`/forms/manage/${f.id}`} className="btn-ghost btn-sm inline-flex items-center gap-1.5 text-grey-500">
                    <Settings2 className="w-3.5 h-3.5" /> Manage
                  </Link>
                </div>
              }
            />
          );
        })}
      </div>
      <div className="px-4 py-3 border-t border-grey-100 text-xs text-grey-500">
        Looking for global forms? <Link to="/forms/manage" className="text-accent hover:underline">Open the form library</Link>.
      </div>
    </Card>
  );
}
